import React, { useRef } from "react";
import { canUseCinematicMotion, gsap } from "../../lib/motion";

type MagneticButtonProps = {
  strength?: number;
  children: React.ReactNode;
  className?: string;
} & (
  | ({
      as?: "button";
      href?: never;
    } & React.ButtonHTMLAttributes<HTMLButtonElement>)
  | ({
      as: "a";
      href: string;
    } & React.AnchorHTMLAttributes<HTMLAnchorElement>)
);

/**
 * Subtle magnetic pull toward the cursor on primary CTAs only.
 */
const MagneticButton: React.FC<MagneticButtonProps> = (props) => {
  const {
    as = "button",
    strength = 0.35,
    children,
    className = "",
    ...rest
  } = props;
  const ref = useRef<HTMLButtonElement | HTMLAnchorElement>(null);

  const handleMove = (e: React.MouseEvent) => {
    if (!canUseCinematicMotion() || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    gsap.to(ref.current, {
      x: x * strength,
      y: y * strength,
      duration: 0.35,
      ease: "power2.out",
    });
  };

  const handleLeave = () => {
    if (!ref.current) return;
    gsap.to(ref.current, {
      x: 0,
      y: 0,
      duration: 0.5,
      ease: "elastic.out(1, 0.4)",
    });
  };

  const sharedClass = `magnetic-btn inline-flex items-center justify-center ${className}`;

  if (as === "a") {
    const anchorProps = rest as React.AnchorHTMLAttributes<HTMLAnchorElement>;
    return (
      <a
        ref={ref as React.RefObject<HTMLAnchorElement>}
        href={props.href}
        className={sharedClass}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        {...anchorProps}
      >
        {children}
      </a>
    );
  }

  const buttonProps = rest as React.ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button
      ref={ref as React.RefObject<HTMLButtonElement>}
      type="button"
      className={sharedClass}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      {...buttonProps}
    >
      {children}
    </button>
  );
};

export default MagneticButton;
